import {
  createBaseThemeOptions,
  palettes,
  createUnifiedTheme,
} from '@backstage/theme';
import { themeColors } from './design-colors';
import {
  commonTypography,
  commonComponents,
  commonSidebarItemRootStyles,
  commonSidebarSubmenuItemStyles,
  commonSidebarLabelStyles,
  commonSidebarLinkResetStyles,
  createTableHeaderOverrides,
} from './common';

export const dtDarkTheme = createUnifiedTheme({
  ...createBaseThemeOptions({
    palette: {
      ...palettes.dark,
      border: themeColors.darkBlueBorder,
      background: {
        default: themeColors.darkBlueBase,
        paper: themeColors.darkBlueSurface,
      },
      text: {
        primary: themeColors.white,
        secondary: themeColors.darkBlueMutedText,
      },
      action: {
        active: themeColors.petrol60,
        hover: 'rgba(0, 103, 127, 0.08)',
        selected: 'rgba(0, 103, 127, 0.16)',
        disabled: themeColors.darkBlueMutedText,
        disabledBackground: themeColors.darkBlueSurface,
      },
      textContrast: themeColors.white,
      textSubtle: themeColors.darkBlueMutedText,

      // Primary brand colors - adjusted for dark theme
      primary: {
        main: themeColors.petrol40,
        light: themeColors.petrol20,
        dark: themeColors.petrol60,
        contrastText: themeColors.white,
      },
      secondary: {
        main: themeColors.petrol40,
        light: themeColors.petrol20,
        dark: themeColors.petrol60,
        contrastText: themeColors.white,
      },

      // Semantic colors - adjusted for dark theme
      error: {
        main: '#f85149',
        light: '#490202',
        dark: '#da3633',
        contrastText: themeColors.white,
      },
      warning: {
        main: '#d29922',
        light: '#341a00',
        dark: '#f2cc60',
        contrastText: themeColors.darkGrey,
      },
      success: {
        main: '#3fb950',
        light: '#0d1117',
        dark: '#56d364',
        contrastText: themeColors.darkGrey,
      },
      info: {
        main: '#58a6ff',
        light: '#0c2d6b',
        dark: '#79c0ff',
        contrastText: themeColors.darkGrey,
      },

      gold: themeColors.yellow,

      navigation: {
        background: themeColors.darkBlueBase,
        indicator: themeColors.petrol60,
        color: themeColors.white,
        selectedColor: themeColors.white,
        navItem: {
          hoverBackground: themeColors.darkBlueSurface,
        },
        submenu: {
          background: themeColors.darkBlueSurface,
        },
      },
      divider: themeColors.darkBlueBorder,
    },
  }),
  typography: commonTypography,
  components: {
    ...commonComponents,
    ...createTableHeaderOverrides({
      idle: themeColors.darkBlueMutedText,
      active: themeColors.white,
    }),
    BackstageHeader: {
      styleOverrides: {
        header: () => ({
          backgroundColor: themeColors.darkBlueBase,
          backgroundImage: `linear-gradient(135deg, ${themeColors.darkBlueBase} 0%, ${themeColors.darkBlueSurfaceAlt} 100%)`,
          minHeight: 112,
          boxShadow: `0 1px 0 ${themeColors.darkBlueBorder}`,
          borderBottom: `1px solid ${themeColors.darkBlueBorder}`,
          // Force all links to be white
          '& a': {
            color: `${themeColors.white} !important`,
            '&:hover': {
              color: `${themeColors.lightGrey20k} !important`,
            },
          },
          // Also target Material-UI components
          '& .MuiLink-root': {
            color: `${themeColors.white} !important`,
            '&:hover': {
              color: `${themeColors.lightGrey20k} !important`,
            },
          },
          '& .MuiTypography-colorInherit': {
            color: `${themeColors.white} !important`,
          },
        }),
        title: {
          color: themeColors.white,
          fontWeight: 700,
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        // Target the stable part of the class names
        '[class*="BackstageItemCardHeader-root"]': {
          backgroundColor: `${themeColors.darkBlueBase} !important`,
          backgroundImage: `linear-gradient(135deg, ${themeColors.darkBlueBase} 0%, ${themeColors.darkBlueSurfaceAlt} 100%) !important`,
          color: `${themeColors.white} !important`,
          '& .MuiTypography-root, & h3, & h4': {
            color: `${themeColors.white} !important`,
          },
        },
        // Also target makeStyles header classes
        '[class*="makeStyles-header"]': {
          backgroundColor: `${themeColors.darkBlueBase} !important`,
          backgroundImage: `linear-gradient(135deg, ${themeColors.darkBlueBase} 0%, ${themeColors.darkBlueSurfaceAlt} 100%) !important`,
          color: `${themeColors.white} !important`,
          '& .MuiTypography-root, & h3, & h4': {
            color: `${themeColors.white} !important`,
          },
        },
        // Target BackstageHeaderLabel value links with multiple selectors
        '[class*="BackstageHeaderLabel-value"] .MuiLink-root': {
          color: `${themeColors.white} !important`,
          textDecoration: 'none !important',
        },
        '[class*="BackstageHeaderLabel-value"] .MuiLink-root:hover': {
          color: `${themeColors.lightGrey20k} !important`,
          textDecoration: 'underline !important',
        },
        '[class*="BackstageHeaderLabel-value"] .MuiLink-underlineHover': {
          color: `${themeColors.white} !important`,
          textDecoration: 'none !important',
        },
        '[class*="BackstageHeaderLabel-value"] .MuiLink-underlineHover:hover': {
          color: `${themeColors.lightGrey20k} !important`,
          textDecoration: 'underline !important',
        },
        // Also target the header area more broadly
        '[class*="BackstageHeader-header"] .MuiLink-root, [class*="BackstageHeader-header"] .MuiLink-underlineHover':
          {
            color: `${themeColors.white} !important`,
          },
        '[class*="BackstageHeader-header"] .MuiLink-root:hover, [class*="BackstageHeader-header"] .MuiLink-underlineHover:hover':
          {
            color: `${themeColors.lightGrey20k} !important`,
          },
        // Target MuiTypography with colorInherit inside header
        '[class*="BackstageHeader-header"] .MuiTypography-colorInherit': {
          color: `${themeColors.white} !important`,
        },
        // Nuclear option - target the specific link path
        'header[class*="BackstageHeader-header"] span[class*="BackstageHeaderLabel-value"] p a':
          {
            color: `${themeColors.white} !important`,
          },
        'header[class*="BackstageHeader-header"] span[class*="BackstageHeaderLabel-value"] p a:hover':
          {
            color: `${themeColors.lightGrey20k} !important`,
          },
        // Also try targeting by specific classes combination
        '.MuiTypography-colorInherit.MuiLink-underlineHover': {
          color: `${themeColors.white} !important`,
        },
        // Global override for all links
        body: {
          '& header[class*="BackstageHeader"] a': {
            color: `${themeColors.white} !important`,
          },
          '& header[class*="BackstageHeader"] a:hover': {
            color: `${themeColors.lightGrey20k} !important`,
          },
        },
      },
    },
    BackstageSidebarSubmenuItem: {
      styleOverrides: {
        item: {
          ...commonSidebarSubmenuItemStyles,
          color: themeColors.white,
          '&:hover': {
            backgroundColor: themeColors.petrol40k,
          },
        },
        selected: {
          backgroundColor: themeColors.darkBlueSurfaceAlt,
          color: themeColors.white,
          fontWeight: 600,
          fontStyle: 'normal',
          '&:hover': {
            backgroundColor: themeColors.petrol40k,
          },
        },
      },
    },
    BackstageSidebarItem: {
      styleOverrides: {
        root: {
          ...commonSidebarItemRootStyles,
          color: themeColors.white,
          '&.Mui-selected': {
            backgroundColor: themeColors.darkBlueSurfaceAlt,
            color: themeColors.white,
            fontWeight: 600,
            fontStyle: 'normal',
            borderLeft: `3px solid ${themeColors.petrol40}`,
            '&:hover': {
              backgroundColor: themeColors.petrol40k,
            },
            '& .MuiListItemIcon-root': {
              color: themeColors.white,
            },
          },
          '&:hover': {
            backgroundColor: themeColors.petrol40k,
            transform: 'translateX(4px)',
          },
          '& .MuiListItemIcon-root': {
            ...commonSidebarItemRootStyles['& .MuiListItemIcon-root'],
            color: themeColors.white,
          },
        },
        label: {
          ...commonSidebarLabelStyles,
        },
      },
    },
    MuiLink: {
      defaultProps: {
        underline: 'hover' as const,
      },
      styleOverrides: {
        root: {
          textDecoration: 'none',
          fontWeight: 500,
          transition: 'all 0.2s ease-in-out',
          color: themeColors.petrol20,
          '&:hover': {
            color: themeColors.petrol10,
            textDecoration: 'underline',
          },
          '&:active': {
            color: themeColors.petrol40,
          },
          ...commonSidebarLinkResetStyles,
          // Override for header context to match lifecycle text
          '.BackstageHeader-header &': {
            color: `${themeColors.white} !important`,
            '&:hover': {
              color: `${themeColors.lightGrey20k} !important`,
            },
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          transition: 'all 0.3s ease-in-out',
          backgroundColor: themeColors.darkBlueSurface,
          boxShadow: `0 0 0 1px ${themeColors.darkBlueBorder}`,
          border: 'none',
          '&:hover': {
            boxShadow: `0 0 0 1px ${themeColors.petrol60}, 0 4px 20px rgba(0, 103, 127, 0.2)`,
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          ...commonComponents.MuiChip.styleOverrides.root,
          color: themeColors.petrol40,
          backgroundColor: 'rgba(0, 103, 127, 0.1)',
          border: `1px solid ${themeColors.petrol60}`,
          '&:hover': {
            backgroundColor: 'rgba(0, 103, 127, 0.2)',
            color: themeColors.petrol20,
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: themeColors.darkBlueSurface,
          // Target template cards specifically
          '&.MuiPaper-elevation1': {
            '& .MuiCardHeader-root': {
              backgroundColor: themeColors.darkBlueBase,
              backgroundImage: `linear-gradient(135deg, ${themeColors.darkBlueBase} 0%, ${themeColors.petrol40k} 100%)`,
              color: themeColors.white,
              '& .MuiCardHeader-title, & .MuiTypography-h5': {
                color: themeColors.white,
              },
              '& .MuiCardHeader-subheader, & .MuiTypography-body2': {
                color: themeColors.darkBlueMutedText,
              },
            },
          },
        },
        elevation1: {
          boxShadow: `0 0 0 1px ${themeColors.darkBlueBorder}`,
        },
        elevation2: {
          boxShadow: `0 0 0 1px ${themeColors.darkBlueBorder}, 0 4px 16px rgba(0, 0, 0, 0.24)`,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 600,
          textTransform: 'none',
          boxShadow: 'none',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            boxShadow: '0 2px 8px rgba(0, 103, 127, 0.2)',
          },
        },
        contained: {
          '&:hover': {
            boxShadow: '0 2px 6px rgba(0, 103, 127, 0.18)',
            transform: 'translateY(-0.5px)',
          },
        },
        containedPrimary: {
          backgroundColor: themeColors.darkBlueSurfaceAlt,
          color: themeColors.white,
          border: `1px solid ${themeColors.darkBlueBorder}`,
          '&:hover': {
            backgroundColor: themeColors.petrol40k,
            borderColor: themeColors.petrol60,
          },
          '&.Mui-disabled': {
            backgroundColor: themeColors.darkBlueSurfaceAlt,
            color: themeColors.darkBlueMutedText,
          },
        },
        outlined: {
          borderColor: themeColors.darkBlueBorder,
          color: themeColors.white,
          '&:hover': {
            borderColor: themeColors.petrol40,
            backgroundColor: 'rgba(0, 103, 127, 0.1)',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            '& fieldset': {
              borderColor: themeColors.darkBlueBorder,
            },
            '&:hover fieldset': {
              borderColor: themeColors.petrol40,
            },
            '&.Mui-focused fieldset': {
              borderColor: themeColors.petrol60,
            },
          },
          '& .MuiInputLabel-root': {
            color: themeColors.darkBlueMutedText,
          },
          '& .MuiOutlinedInput-input': {
            color: themeColors.white,
          },
        },
      },
    },
    // ─── CatalogReactUserListPicker ────────────────────────────────────────
    CatalogReactUserListPicker: {
      styleOverrides: {
        root: {
          backgroundColor: themeColors.darkBlueSurface,
          boxShadow: `0 0 0 1px ${themeColors.darkBlueBorder}`,
          borderRadius: 8,
        },
        title: {
          color: themeColors.darkBlueMutedText,
        },
        listIcon: {
          color: themeColors.petrol40,
        },
        menuItem: {
          color: themeColors.white,
          '&:nth-of-type(odd)': {
            backgroundColor: themeColors.darkBlueSurfaceAlt,
          },
          '&:nth-of-type(even)': {
            backgroundColor: themeColors.darkBlueSurface,
          },
          '&.Mui-selected': {
            backgroundColor: 'rgba(0, 103, 127, 0.20)',
            color: themeColors.petrol40,
            '&:hover': {
              backgroundColor: 'rgba(0, 103, 127, 0.28)',
            },
          },
          '&:hover': {
            backgroundColor: 'rgba(0, 103, 127, 0.10)',
          },
        },
        groupWrapper: {
          backgroundColor: themeColors.darkBlueBase,
          boxShadow: `0 0 0 1px ${themeColors.darkBlueBorder}`,
          borderRadius: 6,
        },
      },
    },
  },
});
